// Téléporteurs (Task 12). Module partagé sans dépendance vers sim.ts / monks.ts
// (il n'importe que state.ts + types.ts) : consommé par le coureur ET le moine sans
// créer de cycle, dans l'esprit « tuiles déclaratives » du plan. Aucun RNG, aucune
// horloge : pur déterminisme.
//
// Verrou (règle FINALE, verrouillée) — pas de minuterie : `teleLockCell` mémorise la
// case d'ARRIVÉE. Une entité au centre exact (x%8===0 && y%8===0) d'une case Teleporter
// se téléporte SSI la case courante n'est pas son `teleLockCell`. À la téléportation on
// pose `teleLockCell = dest` : tant qu'elle reste au centre de la destination elle ne se
// re-téléporte pas (pas de ping-pong immobile). Dès qu'elle est au centre d'une case
// DIFFÉRENTE (elle a quitté la destination), le verrou se ré-arme (`teleLockCell = -1`).
import { cellAt, centerOf, crateAt, GameState } from './state'
import { SUB, T, TELEPORT_CD_TICKS, colOf, rowOf } from './types'

/** Ticks de recharge restants pour la paire qui contient `cell`, 0 si elle est prête (ou inconnue).
 *  Le rendu s'en sert pour éteindre la bouche et dessiner le décompte : une seule source de vérité,
 *  jamais un second calcul côté client qui divergerait de la simulation. */
export function teleCooldownLeft(s: GameState, cell: number): number {
  const idx = s.teleporters.findIndex(([a, b]) => a === cell || b === cell)
  if (idx < 0) return 0
  return Math.max(0, (s.teleReadyAt[idx] ?? 0) - s.tick)
}

/** Fraction de recharge ÉCOULÉE pour la paire qui contient `cell` : 0 juste après usage, 1 quand elle
 *  est de nouveau prête. Sert au décompte visuel (anneau qui se remplit). */
export function teleCooldownRatio(s: GameState, cell: number): number {
  const reste = teleCooldownLeft(s, cell)
  return reste <= 0 ? 1 : 1 - reste / TELEPORT_CD_TICKS
}

export function tryTeleport(s: GameState, e: { x: number; y: number; teleLockCell: number }) {
  // AUDIT 17/07 (« entrer dans un portail sous l'eau ») : le déclencheur est désormais la
  // CASE-CENTRE, plus le centre exact. Au sol c'est strictement équivalent (en marchant, la
  // case-centre ne devient le portail qu'à x%8==0, y étant déjà aligné) ; mais en NAGE la
  // position est quelconque (y mi-frange après une remontée) : on traversait la case portail
  // sans jamais déclencher, on en sortait (poche d'air), on retombait — oscillation sans fin.
  // La téléportation SNAPPE au centre de la destination (déjà le cas) : aucun désalignement
  // ne se propage. Le verrou anti ping-pong (teleLockCell) fonctionne à l'identique.
  const cell = cellAt(e.x, e.y, s.cols)
  if (cell !== e.teleLockCell) e.teleLockCell = -1 // quitté la destination -> ré-armement
  if ((s.terrain[cell] as T) !== T.Teleporter) return
  if (cell === e.teleLockCell) return              // encore posé sur la destination : verrou
  const idx = s.teleporters.findIndex(([a, b]) => a === cell || b === cell)
  if (idx < 0) return
  const pair = s.teleporters[idx]!
  // RECHARGE (30/07) : la paire entière se repose TELEPORT_CD_TICKS après chaque usage, par qui que
  // ce soit — coureur, moine, autre joueur. Sans elle, sortir d'une bouche puis y remettre le pied
  // renvoyait aussitôt à l'autre bout : le moindre pas relançait le voyage (constat Claude-Alain).
  // Les deux bouches s'éteignent ensemble : une seule éteinte laisserait un aller-retour instantané
  // à deux entités, et « le TP est grisé » n'aurait pas de lecture unique à l'écran.
  if ((s.teleReadyAt[idx] ?? 0) > s.tick) return
  const dest = pair[0] === cell ? pair[1] : pair[0]
  if (dest < 0) return                             // téléporteur non apparié : inerte
  if (crateAt(s, colOf(dest, s.cols), rowOf(dest, s.cols))) return // caisse (Phase 4) sur la destination : téléport bloqué (pas de chevauchement)
  const c = centerOf(dest, s.cols)
  e.x = c.x
  e.y = c.y
  e.teleLockCell = dest
  s.teleReadyAt[idx] = s.tick + TELEPORT_CD_TICKS
  s.events.push({ type: 'teleport', from: cell, to: dest })
}
